/**
 * QueueStatusPanel — Batch-Statusliste fuer Deaktivieren/Loeschen (Plan 03-04,
 * ACT-05). Laedt `GET /programmes/artikelleichen/aktionen/status` (KEIN
 * /api/-Prefix, api-Client haengt /api selbst an) beim Mount und nach jedem
 * Wechsel von isRunning true -> false (gleiches wasRunningRef-Muster wie
 * WarnlistePage beim Scan). Zeigt je Batch offen/erledigt/uebersprungen/
 * fehlgeschlagen sowie — bei `fortsetzbar` — einen "Fortsetzen"-Button
 * (manueller Resume, User-Entscheidung, KEIN Auto-Boot-Resume, PROJECT.md).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@libero/core/services/api';
import { Button } from '@libero/core/components/ui/button';
import type { AktionenStatusResponse, BatchStatus } from '../types';
import { standFmt } from '../lib/format';

interface QueueStatusPanelProps {
  isRunning: boolean;
  onFortsetzen: (batch: BatchStatus) => void;
}

const AKTION_LABEL: Record<BatchStatus['aktion'], string> = {
  deaktivieren: 'Deaktivieren',
  loeschen: 'Löschen',
};

export default function QueueStatusPanel({ isRunning, onFortsetzen }: QueueStatusPanelProps) {
  const [batches, setBatches] = useState<BatchStatus[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    return api
      .get('/programmes/artikelleichen/aktionen/status')
      .then((resp: { data: { success: boolean; data: AktionenStatusResponse } }) => {
        if (resp.data.success) setBatches(resp.data.data.batches);
      })
      .catch(() => {
        // Panel ist rein informativ — ein Ladefehler blockiert die Hauptliste nicht.
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const wasRunningRef = useRef(false);
  useEffect(() => {
    if (wasRunningRef.current && !isRunning) {
      load();
    }
    wasRunningRef.current = isRunning;
  }, [isRunning, load]);

  return (
    <div className="rounded-lg border border-[#DAD9D9] bg-white p-4 space-y-3">
      <h2 className="text-sm font-bold text-[#1A1A1A] uppercase tracking-wide">Deaktivieren/Löschen — Warteschlange</h2>

      {loading && batches.length === 0 && <p className="text-sm text-gray-500">Wird geladen…</p>}

      {!loading && batches.length === 0 && <p className="text-sm text-gray-500">Noch keine Aktionen</p>}

      {batches.map((batch) => (
        <div
          key={batch.batchId}
          className="flex items-center justify-between gap-3 flex-wrap border-t border-[#DAD9D9] pt-3 first:border-t-0 first:pt-0"
        >
          <div className="flex items-center gap-3 flex-wrap text-sm">
            <span
              className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-bold uppercase ${
                batch.aktion === 'loeschen' ? 'bg-red-100 text-red-700' : 'bg-[#DAD9D9] text-[#1A1A1A]'
              }`}
            >
              {AKTION_LABEL[batch.aktion]}
            </span>
            <span className="text-[#1A1A1A]">
              {batch.erledigt}/{batch.gesamt} erledigt
            </span>
            <span className="text-gray-500">{batch.offen} offen</span>
            <span className="text-gray-500" title="Durch Schutzregeln übersprungen (PROT-01/04)">
              {batch.uebersprungen} übersprungen
            </span>
            {batch.fehlgeschlagen > 0 && (
              <span className="text-red-700 font-semibold">{batch.fehlgeschlagen} fehlgeschlagen</span>
            )}
            <span className="text-gray-400">Ausgelöst: {standFmt(batch.ausgeloestAm)}</span>
          </div>

          {batch.fortsetzbar && (
            <Button type="button" variant="outline" onClick={() => onFortsetzen(batch)} disabled={isRunning}>
              Fortsetzen
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}
